import {ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation} from "@angular/core";
import {Clipboard} from "@angular/cdk/clipboard";
import {Modal} from "../../../../../services/modal.service";
import {ToastService} from "src/modules/common/services/toast.service";
import * as moment from "moment-timezone";

/**
 * Read-only detail view for a single log row. Renders a structured header +
 * message + a recursive tree for the nested `context`, with a Copy-JSON action
 * and a Pretty/Raw toggle.
 *
 * Works in two modes:
 *  - as a modal (opened via ModalService; reads its row from `modal.params.log`)
 *  - embedded inline via the `[log]` input (e.g. an expanded table row) — in
 *    which case the modal-only heading/close chrome is hidden.
 *
 * Everything is rendered through Angular bindings (auto-escaped) — no
 * bypassSecurityTrustHtml, so arbitrary log payloads can't inject markup.
 */
@Component({
    selector: "admin-logs-view",
    templateUrl: "view.component.html",
    styleUrls: [
        "view.component.scss"
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class LogViewComponent implements OnInit {

    public modal: Modal.IModal;

    /**
     * Log row to display. Bound directly when embedded; populated from the modal
     * params in ngOnInit when opened as a modal.
     */
    @Input()
    public log: any = {};

    public showRaw: boolean = false;

    /**
     * Context values whose rendered text is longer than this are clamped to a
     * few lines and get a Show more / Show less toggle.
     */
    private static readonly COLLAPSE_LIMIT: number = 400;

    /**
     * Dotted paths (e.g. context.mail.body) of the values the user expanded.
     * Keyed by path rather than by value so the state survives the recursive
     * tree being re-rendered.
     */
    private expandedPaths: Set<string> = new Set<string>();

    public constructor(
        private clipboard: Clipboard,
        private toastService: ToastService
    ) {
    }

    public get time(): string {
        return this.log.timestamp
            ? moment.utc(this.log.timestamp).tz("Asia/Jerusalem").format("YYYY-MM-DD HH:mm:ss")
            : "";
    }

    public get hasContext(): boolean {
        return !!this.log.context && Object.keys(this.log.context).length > 0;
    }

    public get rawJson(): string {
        return JSON.stringify(this.log.raw ?? this.log, null, 2);
    }

    public get levelStyle(): { [key: string]: string } {
        switch ((this.log.level || "").toUpperCase()) {
            case "ERROR":
            case "CRITICAL":
            case "ALERT":
            case "EMERGENCY":
                return {color: "#c62828"};
            case "WARNING":
                return {color: "#ef6c00"};
            case "NOTICE":
            case "INFO":
                return {color: "#2e7d32"};
            default:
                return {color: "#757575"};
        }
    }

    public typeOf(value: any): "object" | "array" | "scalar" {
        if (Array.isArray(value)) {
            return "array";
        }
        if (value !== null && typeof value === "object") {
            return "object";
        }
        return "scalar";
    }

    /**
     * Rows of an object node. Each carries its full dotted path (used as the
     * label, the expand key and the *ngFor identity) and whether its value is
     * long enough to be collapsed — computed once here rather than per binding.
     */
    public entries(value: any, path: string): { key: string, path: string, value: any, long: boolean }[] {
        return Object.keys(value).map((key: string): { key: string, path: string, value: any, long: boolean } => {
            const entryPath: string = path ? `${path}.${key}` : key;

            return {key, path: entryPath, value: value[key], long: this.isLong(value[key])};
        });
    }

    public trackByPath(index: number, entry: { path: string }): string {
        return entry.path;
    }

    public isExpanded(path: string): boolean {
        return this.expandedPaths.has(path);
    }

    public toggle(path: string): void {
        if (this.expandedPaths.has(path)) {
            this.expandedPaths.delete(path);
        } else {
            this.expandedPaths.add(path);
        }
    }

    /**
     * Is the value bulky enough to hide behind a toggle? Scalars are measured as
     * displayed (an email body, a stack trace); objects/arrays by their JSON.
     */
    private isLong(value: any): boolean {
        const text: string = this.typeOf(value) === "scalar"
            ? this.display(value)
            : (JSON.stringify(value) ?? "");

        return text.length > LogViewComponent.COLLAPSE_LIMIT;
    }

    public display(value: any): string {
        if (value === null || value === undefined) {
            return "null";
        }
        if (typeof value === "boolean") {
            return value ? "true" : "false";
        }
        return String(value);
    }

    public copy(): void {
        this.clipboard.copy(this.rawJson);
        this.toastService.show("Copied to clipboard", "success");
    }

    public close(): void {
        this.modal.response.emit({name: "close"});
    }

    public ngOnInit(): void {
        if (this.modal?.params?.log) {
            this.log = this.modal.params.log;
        }
    }
}
